
import React, { useState } from "react";
import { FriendWithStatus } from "@/types";
import { PlusCircle, UserPlus, Trash2, Mail, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import { toast } from "sonner";
import { checkEmailExists } from "@/integrations/supabase/manualPlayerService";

interface ConsolidatedFriendsTabContentProps {
  friends: FriendWithStatus[];
  pendingRequests: FriendWithStatus[];
  sentRequests: FriendWithStatus[];
  isLoading: boolean;
  onOpenAddFriendModal: () => void;
  onAccept: (friendshipId: string) => void;
  onReject: (friendshipId: string) => void;
  onCancel: (friendshipId: string) => void;
  onRemoveManualFriend?: (manualPlayerId: string) => void;
  onInviteManualPlayer?: (manualPlayerId: string, email: string) => Promise<void>;
  invitedPlayerIds?: Set<string>;
}

const ConsolidatedFriendsTabContent = ({
  friends,
  pendingRequests,
  sentRequests,
  isLoading,
  onOpenAddFriendModal,
  onAccept,
  onReject,
  onCancel,
  onRemoveManualFriend,
  onInviteManualPlayer,
  invitedPlayerIds,
}: ConsolidatedFriendsTabContentProps) => {
  const [invitingPlayerId, setInvitingPlayerId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  const handleInlineInvite = async (manualPlayerId: string) => {
    const trimmed = inviteEmail.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSendingInvite(true);
    try {
      // Check if this email belongs to an existing account
      const { exists } = await checkEmailExists(trimmed);
      if (exists) {
        toast.info("This email has an account — send them a friend request instead");
        setIsSendingInvite(false);
        return;
      }

      if (onInviteManualPlayer) {
        await onInviteManualPlayer(manualPlayerId, trimmed);
      }
      setInvitingPlayerId(null);
      setInviteEmail("");
    } catch {
      toast.error("Failed to send invite");
    } finally {
      setIsSendingInvite(false);
    }
  };

  const allConnections = [
    ...friends.map(friend => ({ ...friend, type: 'friend' as const })),
    ...pendingRequests.map(request => ({ ...request, type: 'request' as const })),
    ...sentRequests.map(sent => ({ ...sent, type: 'sent' as const })),
  ];

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array(3).fill(0).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full shrink-0" />
              <div className="flex-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-44 mt-1.5" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (allConnections.length === 0) {
    return (
      <div className="text-center py-12">
        <UserPlus className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No connections yet</p>
        <Button
          className="mt-4 bg-forest hover:bg-forest/90"
          onClick={onOpenAddFriendModal}
        >
          <PlusCircle className="h-4 w-4 mr-2" />
          Add your first friend
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {allConnections.map(connection => (
        <Card
          key={`${connection.id}-${connection.type}`}
          className="hover:shadow-md transition-all"
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <AvatarPlaceholder user={connection} />
              <div>
                <h3 className="font-medium">{connection.name}</h3>
                {connection.email && (
                  <p className="text-sm text-muted-foreground">{connection.email}</p>
                )}
                {(connection.type !== 'friend' || connection.isManual || connection.isSample) && (
                  <div className="mt-1">
                    {connection.isSample && (
                      <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                        Sample
                      </Badge>
                    )}
                    {connection.isManual && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        No account
                      </Badge>
                    )}
                    {connection.type === 'request' && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-none">
                        Friend request
                      </Badge>
                    )}
                    {connection.type === 'sent' && (
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-none">
                        Request sent
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {connection.type === 'request' && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-forest border-forest hover:bg-forest/5"
                    onClick={() => {
                      if (connection.friendshipId) {
                        onAccept(connection.friendshipId);
                      }
                    }}
                  >
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    onClick={() => {
                      if (connection.friendshipId) {
                        onReject(connection.friendshipId);
                      }
                    }}
                  >
                    Decline
                  </Button>
                </>
              )}
              {connection.type === 'sent' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-600 hover:bg-red-50"
                  onClick={() => {
                    if (connection.friendshipId) {
                      onCancel(connection.friendshipId);
                    }
                  }}
                >
                  Cancel
                </Button>
              )}
              {connection.type === 'friend' && connection.isManual && connection.manualPlayerId && (
                <>
                  {invitedPlayerIds?.has(connection.manualPlayerId) ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Invited
                    </Badge>
                  ) : invitingPlayerId === connection.manualPlayerId ? (
                    <div className="flex items-center gap-1">
                      <Input
                        type="email"
                        placeholder="Email address"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleInlineInvite(connection.manualPlayerId!)}
                        className="h-8 w-40 text-sm"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-forest border-forest hover:bg-forest/5"
                        onClick={() => handleInlineInvite(connection.manualPlayerId!)}
                        disabled={isSendingInvite}
                      >
                        <Send className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2"
                        onClick={() => { setInvitingPlayerId(null); setInviteEmail(""); }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : onInviteManualPlayer && (
                    connection.email ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-forest border-forest hover:bg-forest/5"
                        onClick={() => onInviteManualPlayer(connection.manualPlayerId!, connection.email!)}
                      >
                        <Mail className="h-3.5 w-3.5 mr-1" />
                        Invite
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-forest border-forest hover:bg-forest/5"
                        onClick={() => setInvitingPlayerId(connection.manualPlayerId!)}
                      >
                        <Mail className="h-3.5 w-3.5 mr-1" />
                        Invite
                      </Button>
                    )
                  )}
                  {onRemoveManualFriend && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-600 hover:bg-red-50"
                      onClick={() => onRemoveManualFriend(connection.manualPlayerId!)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ConsolidatedFriendsTabContent;
